/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { InteractiveComponents } from '../../../../core/enums';
import { LocaleKeys } from '../../../../core/locale';
import { Locale } from '../../../../core/locale/Locale';
import { ScreenTaskControllerTypes } from '../../../../core/navigation';
import { setActiveInteractiveComponentAction } from '../../../interactivelayer/interactivelayer.actions';
import { clearActiveInteractiveComponent } from '../../../interactivelayer/interactiveLayer.reducers';
import { onMouseClickNoNetworkOverlay } from '../../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    hideOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../../overlaydialog/overlaydialog.reducers';
import {
    setCaretVisibilityAction,
    setVerticalPositionRecordingActivityAction,
} from '../recordingactivity.actions';
import {
    disableGhostModeRecordingActivity,
    enableGhostModeRecordingActivity,
    handleRecordingActivityErrorOrNetwork,
    HISTORY_INFO_MOUSE_TRAP,
    onKeyOkNoTaskRecordingActivity,
} from '../recordingactivity.reducers';
import {
    onKeyChannelDownRecordingActivity,
    onKeyChannelUpRecordingActivity,
    onLongPressOkRecordingActivity,
} from './recordingactivity.navigation.reducers';

const getHighlightPosition = (value: string | null | undefined): number | null => {
    if (!value) return null;

    const verticalStr = value.split('_')?.[1];
    if (!verticalStr) return null;

    const vertical = Number(verticalStr);

    if (Number.isNaN(vertical)) return null;

    return vertical;
};

export const onMouseClickRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { activeComponent } = getState().interactiveLayer;
    const { value } = getState().inputFactory;
    const { isError } = getState().recordingActivity;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

            dispatch(defaultMouseClickTasks(activeComponent, value));
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseClickNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(onMouseOkDeleteConflictOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOkLongPressRecordingActivity =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        const highlightPosition = getHighlightPosition(value);
        const { isError } = getState().recordingActivity;

        if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

        if (highlightPosition || highlightPosition === 0) {
            dispatch(onLongPressOkRecordingActivity());
        }
    };

export const onMouseOverRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(defaultMouseOverTasks());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOutRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(defaultMouseOutTasks());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;

        default:
    }
};

const defaultMouseOverTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { ghostMode } = getState().recordingActivity;

    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setActiveInteractiveComponentAction(value));
            dispatch(setCaretVisibilityAction(true));
            dispatch(enableGhostModeRecordingActivity());
            break;
        default:
    }

    const highlightPosition = getHighlightPosition(value);

    if (!highlightPosition && highlightPosition !== 0) {
        dispatch(enableGhostModeRecordingActivity());
        return;
    }
    if (ghostMode) dispatch(disableGhostModeRecordingActivity());

    dispatch(setVerticalPositionRecordingActivityAction(highlightPosition));
};

const defaultMouseOutTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    dispatch(enableGhostModeRecordingActivity());
    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setCaretVisibilityAction(false));
            dispatch(clearActiveInteractiveComponent());
            break;
        default:
    }
};

const defaultMouseClickTasks =
    (activeInteractiveComponent: InteractiveComponents, inputFactoryValue: string) =>
    (dispatch: Function) => {
        if (activeInteractiveComponent === InteractiveComponents.SOUTH) {
            dispatch(onKeyChannelDownRecordingActivity());
            return;
        }
        if (activeInteractiveComponent === InteractiveComponents.NORTH) {
            dispatch(onKeyChannelUpRecordingActivity());
            return;
        }

        const highlightPosition = getHighlightPosition(inputFactoryValue);
        if (highlightPosition || highlightPosition === 0) {
            dispatch(onKeyOkNoTaskRecordingActivity());
        }
    };

const onMouseOkDeleteConflictOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    const { verticalPosition } = getState().overlayHighlight;
    const { uiProperties } = getState().overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];

    // Value 0 = Ok button
    if (Number(value) === 0 || value === HISTORY_INFO_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    if (
        Number(value) === 1 && // Value 1 = Recover recording button
        selectedOption.text === Locale.getString(LocaleKeys.RECORDING_ACTIVITY_GO_TO_RECOVER)
    ) {
        dispatch(hideOverlayDialog());
        dispatch(onKeyOkNoTaskRecordingActivity(true));
    }
};
